/**
 * Shared display guardrails for itinerary rendering.
 * Ported from web's createDayTimeline() (~3,800 lines) into reusable,
 * platform-agnostic pure functions. Used by mobile's post-processing
 * pipeline and potentially by web for refactoring.
 *
 * These functions validate and fix AI-generated day data AFTER
 * normalizeDay/validateAndFixDay, BEFORE rendering.
 */

import { parseTimeToMinutes } from './timeUtils.js';
import {
  lookupCityAirportCode,
  cleanIataCode,
  CITY_TO_AIRPORT_CODE,
  TRAIN_FRIENDLY_AIRPORT_ROUTES,
} from './transportConstants.js';
import { isIslandCity, getCityCountrySync, getContinentForCountry } from './geographyConstants.js';
import { parseDurationToMinutes } from './itineraryHelpers.js';
import { sanitizeBaseCity } from './itineraryNormalizer.js';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Known airport code hallucinations from AI models. Maps wrong → correct. */
const AIRPORT_HALLUCINATION_MAP = {
  FAOR: 'JNB',  // Johannesburg OR Tambo
  SDC: 'LIS',   // Lisbon — AI hallucinates "SDC" for Lisbon
  PRT: 'OPO',   // Porto — AI hallucinates "PRT"
  ROM: 'FCO',   // Rome — IATA is FCO (Fiumicino) or CIA
  MIL: 'MXP',   // Milan — IATA is MXP (Malpensa) or LIN
  FAR: 'FAO',   // Faro — AI sometimes uses "FAR" instead of "FAO"
  MOW: 'SVO',   // Moscow — city code, airport is SVO or DME
  SPB: 'LED',   // St. Petersburg — LED is Pulkovo
  LON: 'LHR',   // London — city code, airport is LHR/LGW/STN
  PAR: 'CDG',   // Paris — city code, airport is CDG/ORY
  NYC: 'JFK',   // New York — city code, airport is JFK/LGA/EWR
  BUE: 'EZE',   // Buenos Aires — city code, airport is EZE
  TYO: 'NRT',   // Tokyo — city code, airport is NRT/HND
  OSA: 'KIX',   // Osaka — city code, airport is KIX/ITM
  STO: 'ARN',   // Stockholm — city code, airport is ARN
  MRK: 'RAK',   // Marrakech — AI hallucinates "MRK"
};

/** Activities that duplicate programmatic arrival cards on Day 1. */
const ARRIVAL_ACTIVITY_PATTERNS = [
  'arrival', 'immigration', 'customs', 'landing',
  'check-in to hotel', 'hotel check-in', 'transit to hotel', 'transfer to hotel',
];

/** Activities that duplicate programmatic departure cards on Last Day. */
const DEPARTURE_ACTIVITY_PATTERNS = [
  'transit to airport', 'transfer to airport', 'airport departure',
  'hotel checkout', 'check out', 'check-out', 'departure',
];

/** Inter-city travel patterns — flights use broad match, others contiguous only. */
const INTER_CITY_TRAVEL_PATTERNS = [
  /\bflight\b.*\bto\b/i, /\bfly\b.*\bto\b/i,
  /\bflight\s+from\b/i, /\bfly\s+from\b/i,
  /\btransfer\s+to\b/i, /\btravel\s+to\b/i,
  /\bdrive\s+to\b/i, /\bbus\s+to\b/i, /\btrain\s+to\b/i,
  /\bdepart\s+for\b/i, /\bhead\s+to\s+airport\b/i,
];

/** Keywords that legitimately have 1-5 AM activity times. */
const EARLY_MORNING_KEYWORDS = [
  'sunrise', 'dawn', 'early morning', 'airport', 'flight',
  'departure', 'checkout', 'check-out', 'game drive',
];

// ─── P1: Activity Filtering ────────────────────────────────────────────────

/**
 * Filter out AI-generated logistics items that duplicate programmatic cards.
 * On Day 1: removes arrival/customs/immigration/hotel-check-in items.
 * On Last Day: removes departure/checkout/airport-transit items.
 * On inter-city travel days: removes travel activity items.
 *
 * @param {Array} schedule - Day's schedule array from AI
 * @param {Object} context
 * @param {boolean} context.isFirstDay
 * @param {boolean} context.isLastDay
 * @param {boolean} context.isInterCityTravelDay
 * @param {string} [context.prevCity] - Previous day's city (for wrong-city filtering)
 * @param {string} [context.currentCity] - Current day's city
 * @returns {Array} Filtered schedule
 */
export function filterDuplicateLogisticsItems(schedule, context = {}) {
  if (!Array.isArray(schedule)) return schedule || [];

  const { isFirstDay, isLastDay, isInterCityTravelDay, prevCity, currentCity } = context;
  const prevCityLower = (prevCity || '').toLowerCase();
  const currentCityLower = (currentCity || '').toLowerCase();

  return schedule.filter(item => {
    const activityLower = (item.activity || '').toLowerCase();

    // Day 1: filter arrival duplicates
    if (isFirstDay) {
      const isArrivalDupe = ARRIVAL_ACTIVITY_PATTERNS.some(p => activityLower.includes(p));
      if (isArrivalDupe) return false;
    }

    // Last Day: filter departure duplicates
    if (isLastDay) {
      const isDepartureDupe = DEPARTURE_ACTIVITY_PATTERNS.some(p => activityLower.includes(p));
      if (isDepartureDupe) return false;
    }

    // Inter-city travel days: filter travel activities
    if (isInterCityTravelDay) {
      const isTravelActivity = INTER_CITY_TRAVEL_PATTERNS.some(p => p.test(activityLower));
      if (isTravelActivity) return false;

      // Wrong-city filtering: activities mentioning previous city
      if (prevCityLower && currentCityLower) {
        const location = (item.location || item.address || '').toLowerCase();
        if (
          (location.includes(prevCityLower) || activityLower.includes(prevCityLower)) &&
          !activityLower.includes('flight') && !activityLower.includes('travel')
        ) {
          return false;
        }
      }
    }

    return true;
  });
}

/**
 * Cap activities on travel/arrival days to prevent overpacked schedules.
 * Travel days have limited free time — max N activities.
 *
 * @param {Array} schedule - Day's schedule array (already filtered)
 * @param {Object} context
 * @param {boolean} context.isTravelDay - Is this a travel or arrival day?
 * @param {number} [context.maxActivities=3] - Max activities allowed
 * @returns {Array} Capped schedule
 */
export function capTravelDayActivities(schedule, context = {}) {
  if (!Array.isArray(schedule) || !context.isTravelDay) return schedule || [];

  const max = context.maxActivities || 3;
  let count = 0;

  return schedule.filter(item => {
    // Meals don't count toward cap
    const isMeal = (item.type === 'meal' || item.type === 'food' ||
                    item.type === 'breakfast' || item.type === 'lunch' || item.type === 'dinner');
    if (isMeal) return true;

    count++;
    return count <= max;
  });
}

// ─── P1: Time Validation ───────────────────────────────────────────────────

/**
 * Fix AM/PM time errors in schedule items.
 * - Activities at 1:00-5:59 AM → shift to PM (unless early-morning keyword)
 * - Breakfast at 1:00-5:59 AM → clamp to 07:30
 * - Lunch/dinner at 1:00-5:59 AM → shift to PM
 * - Time-of-day label correction ("Morning Run" at 2 PM → "Afternoon Run")
 *
 * Mutates items in-place and returns the same array.
 *
 * @param {Array} items - Schedule + meals items
 * @returns {Array} Same array with times fixed
 */
export function fixAMPMTimes(items) {
  if (!Array.isArray(items)) return items || [];

  for (const item of items) {
    let minutes = parseTimeToMinutes(item.time);
    if (minutes === undefined || minutes === null) continue;

    // Fix 1:00-5:59 AM activities
    if (minutes >= 60 && minutes < 360) {
      const actLower = (item.activity || '').toLowerCase();
      const mealLower = (item.meal || item.type || '').toLowerCase();

      // Breakfast: clamp to 07:30
      if (mealLower.includes('breakfast') || actLower.includes('breakfast')) {
        item.time = '07:30';
        continue;
      }

      // Non-meal: check for early-morning keywords
      const isEarlyMorning = EARLY_MORNING_KEYWORDS.some(kw => actLower.includes(kw));
      if (!isEarlyMorning) {
        // Shift to PM
        minutes += 12 * 60;
        item.time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
      }
    }

    // Time-of-day label correction
    if (item.activity) {
      const hour = Math.floor(parseTimeToMinutes(item.time) / 60);
      if (hour >= 12 && /\bmorning\b/i.test(item.activity)) {
        item.activity = item.activity.replace(/\bmorning\b/gi, hour < 17 ? 'Afternoon' : 'Evening');
      } else if (hour < 12 && /\bevening\b/i.test(item.activity)) {
        item.activity = item.activity.replace(/\bevening\b/gi, 'Morning');
      } else if ((hour < 12 || hour >= 17) && /\bafternoon\b/i.test(item.activity)) {
        item.activity = item.activity.replace(/\bafternoon\b/gi, hour < 12 ? 'Morning' : 'Evening');
      }
    }
  }

  return items;
}

// ─── P1: Airport Code Validation ───────────────────────────────────────────

/**
 * Extract a 3-letter IATA code from a free-text airport string.
 * Handles: "NCE", "Nice (NCE)", "KMIA" (FAA prefix), full names.
 *
 * @param {string} str - Airport code/name string
 * @returns {string} 3-letter IATA code or empty string
 */
export function extractAirportCode(str) {
  if (!str) return '';
  const t = str.trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(t)) return t;
  // Parenthesized: "Nice Airport (NCE)"
  const m = str.match(/\(([A-Z]{3})\)/);
  if (m) return m[1];
  // 4-letter FAA codes: "KMIA" → "MIA"
  if (/^[A-Z]{4}$/.test(t) && t.startsWith('K')) {
    return t.substring(1);
  }
  return '';
}

/**
 * Validate an airport code against known IATA codes and hallucination map.
 * Falls back to city-name lookup if code is unknown.
 *
 * @param {string} code - 3-letter airport code to validate
 * @param {string} [cityFallback] - City name for fallback lookup
 * @returns {string} Validated IATA code
 */
export function validateAirportCode(code, cityFallback) {
  if (!code) return lookupCityAirportCode(cityFallback) || '';

  const upper = code.toUpperCase();

  // Check hallucination map first
  if (AIRPORT_HALLUCINATION_MAP[upper]) {
    return AIRPORT_HALLUCINATION_MAP[upper];
  }

  if (!/^[A-Z]{3}$/.test(upper)) {
    return lookupCityAirportCode(cityFallback) || '';
  }

  // Check against known codes
  const knownCodes = new Set(Object.values(CITY_TO_AIRPORT_CODE));
  if (knownCodes.has(upper)) return upper;

  // Unknown 3-letter code — try city-based fallback
  const cityCode = lookupCityAirportCode(cityFallback);
  return cityCode || upper; // Prefer known city code, fall back to original
}

/**
 * Validate and fix all airport codes in a day's flight/transit items.
 * Processes the schedule array, correcting airport codes in flight items.
 *
 * @param {Array} schedule - Day's schedule/transport items
 * @param {Object} context
 * @param {string} [context.fromCity] - Origin city for this leg
 * @param {string} [context.toCity] - Destination city for this leg
 * @returns {Array} Same array with airport codes validated
 */
export function validateFlightAirportCodes(schedule, context = {}) {
  if (!Array.isArray(schedule)) return schedule || [];

  for (const item of schedule) {
    if (!item) continue;

    // Fix airport codes in flight items
    if (item.type === 'flight' || item.isArrivalFlightCard) {
      if (item.airportCode) {
        item.airportCode = validateAirportCode(
          extractAirportCode(item.airportCode),
          context.toCity
        );
      }
      if (item.homeAirportCode) {
        item.homeAirportCode = validateAirportCode(
          extractAirportCode(item.homeAirportCode),
          context.fromCity
        );
      }
    }

    // Fix from/to airport fields in inter-city transport items
    if (item.from?.airport) {
      const extracted = extractAirportCode(item.from.airport);
      const validated = validateAirportCode(extracted, item.from?.city || context.fromCity);
      if (validated) item.from.airport = validated;
    }
    if (item.to?.airport) {
      const extracted = extractAirportCode(item.to.airport);
      const validated = validateAirportCode(extracted, item.to?.city || context.toCity);
      if (validated) item.to.airport = validated;
    }

    // Fix standalone airportCode field
    if (item.airportCode && !item.isArrivalFlightCard) {
      item.airportCode = validateAirportCode(
        extractAirportCode(item.airportCode),
        context.toCity || context.fromCity
      );
    }
  }

  return schedule;
}

// ─── P2: Arrival / Departure / Recovery Items ──────────────────────────────

/**
 * Create arrival items to prepend to Day 1 timeline.
 *
 * @param {Object} dayData - Day data from AI
 * @param {Object} context
 * @param {boolean} context.isDomestic - Domestic vs international flight
 * @param {string} [context.arrivalAirportCode] - Arrival airport IATA code
 * @param {string} [context.arrivalAirportName] - Full airport name
 * @param {string} [context.homeAirportCode] - Departure airport code
 * @param {string} [context.arrivalTime] - Arrival time string (e.g. "14:00")
 * @param {string} [context.hotelName] - Hotel name for check-in card
 * @param {string} [context.destinationName] - Destination name for car rental suggestion
 * @returns {Array} Array of items to prepend to timeline
 */
export function createArrivalItems(dayData, context = {}) {
  const items = [];
  const {
    isDomestic, arrivalAirportCode: rawArrivalCode, arrivalAirportName,
    homeAirportCode, arrivalTime, hotelName, destinationName,
  } = context;
  // B9B: Fallback to IATA lookup if no arrival airport code provided
  const arrivalAirportCode = rawArrivalCode || lookupCityAirportCode(destinationName) || '';

  const arrivalMinutes = parseTimeToMinutes(arrivalTime) || 600; // Default 10:00 AM

  if (!isDomestic) {
    // International: Customs + Immigration (90 min)
    const customsEnd = arrivalMinutes + 90;
    items.push({
      type: 'arrival-customs',
      time: minutesToTimeStr(arrivalMinutes),
      endTime: minutesToTimeStr(customsEnd),
      activity: arrivalAirportName
        ? `Arrival at ${arrivalAirportName} & Immigration`
        : arrivalAirportCode
          ? `Arrival at ${arrivalAirportCode} & Immigration`
          : 'Arrival & Immigration',
      duration: '1.5 hours',
      description: homeAirportCode && arrivalAirportCode
        ? `Flight from ${homeAirportCode} to ${arrivalAirportCode}${arrivalAirportName ? ` (${arrivalAirportName})` : ''} - immigration and customs process`
        : arrivalAirportCode
          ? `International arrival at ${arrivalAirportCode}${arrivalAirportName ? ` (${arrivalAirportName})` : ''} - immigration and customs process`
          : 'International arrival and immigration process',
      location: homeAirportCode && arrivalAirportCode
        ? `${homeAirportCode} → ${arrivalAirportCode}`
        : arrivalAirportName || (arrivalAirportCode ? `${arrivalAirportCode} Airport` : undefined),
      sortTime: arrivalMinutes,
      airportCode: arrivalAirportCode || null,
      homeAirportCode: homeAirportCode || null,
      isArrivalFlightCard: true,
    });
  } else {
    // Domestic: Baggage claim (30 min)
    const baggageEnd = arrivalMinutes + 30;
    const destLower = (destinationName || '').toLowerCase();
    const needsCar = ['key', 'hawaii', 'park', 'beach', 'coast', 'route']
      .some(kw => destLower.includes(kw));

    items.push({
      type: 'arrival-domestic',
      time: minutesToTimeStr(arrivalMinutes),
      endTime: minutesToTimeStr(baggageEnd),
      activity: arrivalAirportName
        ? `Arrival at ${arrivalAirportName}`
        : arrivalAirportCode
          ? `Arrival at ${arrivalAirportCode}`
          : 'Baggage Claim',
      duration: '30 minutes',
      description: homeAirportCode && arrivalAirportCode
        ? `Flight from ${homeAirportCode} to ${arrivalAirportCode}. Collect checked bags.${needsCar ? ' Tip: Consider renting a car at the airport for easier exploration.' : ''}`
        : needsCar
          ? `Collect checked bags${arrivalAirportCode ? ` at ${arrivalAirportCode}` : ''}. Tip: Consider renting a car at the airport for easier exploration.`
          : `Collect checked bags${arrivalAirportCode ? ` at ${arrivalAirportCode}` : ''}`,
      location: homeAirportCode && arrivalAirportCode
        ? `${homeAirportCode} → ${arrivalAirportCode}`
        : arrivalAirportName || (arrivalAirportCode ? `${arrivalAirportCode} Airport` : undefined),
      sortTime: arrivalMinutes,
      airportCode: arrivalAirportCode || null,
      homeAirportCode: homeAirportCode || null,
      isArrivalFlightCard: true,
    });
  }

  // Hotel check-in card
  const customsTime = isDomestic ? 30 : 90;
  const transitTime = 45; // Default city transit
  const checkinMinutes = arrivalMinutes + customsTime + transitTime;

  if (hotelName) {
    items.push({
      type: 'hotel-checkin',
      time: minutesToTimeStr(checkinMinutes),
      activity: `Check in to ${hotelName}`,
      duration: '30 minutes',
      description: 'Check in, freshen up, and settle into your room',
      location: hotelName,
      sortTime: checkinMinutes,
    });
  }

  return items;
}

/**
 * Create a recovery note item after overnight travel.
 *
 * @param {Object} dayData
 * @param {Object} context
 * @param {boolean} context.isOvernightFlight - Was it an overnight flight?
 * @param {boolean} context.isOvernightTrain - Was it an overnight train/bus?
 * @param {string} [context.hotelName] - Hotel name
 * @param {string} [context.currentCity] - Current city
 * @param {number} [context.arrivalMinutes] - Arrival time in minutes
 * @returns {Object|null} Recovery note item, or null if not applicable
 */
export function createRecoveryNote(dayData, context = {}) {
  const {
    isOvernightFlight, isOvernightTrain, hotelName, currentCity, arrivalMinutes,
    isLongHaulFlight, arrivalDayOffset, timezoneDiff, travelDuration,
  } = context;

  // Jet lag adjustment for same-day long-haul arrivals with big timezone change
  // (e.g. HK→NYC: 16h flight but arrives same day due to -13h timezone shift)
  if (!isOvernightFlight && !isOvernightTrain && isLongHaulFlight && arrivalDayOffset <= 0 && Math.abs(timezoneDiff || 0) >= 5) {
    const jetLagMinutes = (arrivalMinutes || 600) + 15;
    return {
      type: 'recovery-note',
      time: minutesToTimeStr(jetLagMinutes),
      activity: '⏰ Jet Lag Adjustment',
      description: `After ${travelDuration ? `a ${travelDuration} flight` : 'a long-haul flight'} with a ${Math.abs(timezoneDiff)}-hour time zone change, your body clock will need adjusting. Stay hydrated and get some sunlight to help reset your circadian rhythm.`,
      location: hotelName || currentCity || 'Hotel',
      duration: '2-3 hours',
      sortTime: jetLagMinutes,
      isRecoveryNote: true,
    };
  }

  if (!isOvernightFlight && !isOvernightTrain) return null;

  const customsTime = isOvernightFlight ? 90 : 0;
  const recoveryMinutes = (arrivalMinutes || 600) + customsTime + 30; // After customs + transit

  if (isOvernightFlight) {
    return {
      type: 'recovery-note',
      time: minutesToTimeStr(recoveryMinutes),
      activity: '💤 Rest & Recovery Time',
      description: 'After the overnight flight, consider resting before activities. Stay hydrated and get some sunlight to adjust to the new timezone faster.',
      location: hotelName || 'Hotel',
      duration: '2-3 hours',
      sortTime: recoveryMinutes,
      isRecoveryNote: true,
      cutoffMinutes: recoveryMinutes + 120, // No activities for 2h after recovery start
    };
  }

  // Trains/buses: shorter recovery
  return {
    type: 'recovery-note',
    time: minutesToTimeStr(recoveryMinutes),
    activity: '☕ Freshen Up & Settle In',
    description: `After the overnight journey, take some time to freshen up and get oriented in ${currentCity || 'your destination'}.`,
    location: hotelName || 'Hotel',
    duration: '1 hour',
    sortTime: recoveryMinutes,
    isRecoveryNote: true,
    cutoffMinutes: recoveryMinutes + 60, // No activities for 1h after recovery
  };
}

/**
 * Create departure-day items (checkout, transit, breakfast injection).
 *
 * @param {Object} dayData
 * @param {Object} context
 * @param {string} [context.departureTime] - Departure/flight time string
 * @param {string} [context.hotelName] - Hotel name
 * @param {string} [context.departureAirportCode] - Departure airport code
 * @param {string} [context.currentCity] - Current city
 * @param {number} [context.transitDurationMinutes] - Transit to airport duration
 * @param {boolean} [context.isIsland] - Is current location an island?
 * @param {boolean} [context.isDomestic] - Is this a domestic trip?
 * @param {boolean} [context.hasBreakfast] - Does timeline already have breakfast?
 * @param {Array} [context.existingItems] - Current timeline items for dedup
 * @returns {Array} Array of items to append to timeline
 */
export function createDepartureItems(dayData, context = {}) {
  const items = [];
  const {
    departureTime, hotelName, departureAirportCode: rawDepartureAirportCode, currentCity,
    transitDurationMinutes, isIsland, isDomestic, hasBreakfast, existingItems,
  } = context;
  // B9B: Fallback to IATA lookup if no airport code provided
  const departureAirportCode = rawDepartureAirportCode || lookupCityAirportCode(currentCity) || '';

  const departMinutes = parseTimeToMinutes(departureTime) || (18 * 60); // Default 6 PM

  // Transit duration: island → flight (150 min), else use provided or default 45 min
  let transitDuration = transitDurationMinutes || 45;
  let transitMode = 'drive';
  if (isIsland) {
    transitDuration = 150; // Island connecting flight
    transitMode = 'flight';
  }

  // Work backwards: flight - airport buffer - transit - 30min checkout
  const airportBuffer = isDomestic ? 120 : 180; // 2hr domestic / 3hr international
  const totalLeadTime = airportBuffer + transitDuration + 30;
  let checkoutMinutes = departMinutes - totalLeadTime;

  // Handle edge case: checkout would be negative (very early/overnight flights)
  const isOvernightCheckout = checkoutMinutes < 0;
  if (isOvernightCheckout) checkoutMinutes = Math.max(0, checkoutMinutes);

  const isVeryEarlyCheckout = checkoutMinutes < 300 && checkoutMinutes >= 0; // Before 5 AM
  const isLateCheckout = checkoutMinutes > 840; // After 2 PM

  const checkoutDescription = isOvernightCheckout
    ? 'Very early checkout for red-eye flight - consider staying near airport'
    : isVeryEarlyCheckout
      ? 'Early checkout required for your flight'
      : isLateCheckout
        ? 'Late checkout needed - request in advance or use luggage storage'
        : 'Check out and gather luggage';

  // Inject breakfast if missing and there's time
  if (!hasBreakfast && !isVeryEarlyCheckout && !isOvernightCheckout && checkoutMinutes > 90) {
    const breakfastMinutes = checkoutMinutes - 90;
    items.push({
      type: 'meal',
      meal: 'Breakfast',
      time: minutesToTimeStr(breakfastMinutes),
      activity: 'Breakfast',
      restaurant: hotelName ? `${hotelName} Restaurant` : 'Hotel Restaurant',
      description: 'Enjoy breakfast before checking out',
      duration: '1 hour',
      location: hotelName || 'Hotel',
      sortTime: breakfastMinutes,
    });
  }

  // Checkout card
  items.push({
    type: 'checkout',
    time: minutesToTimeStr(checkoutMinutes),
    activity: '🏨 Hotel Checkout',
    description: checkoutDescription,
    duration: '30 minutes',
    location: hotelName || 'Hotel',
    sortTime: checkoutMinutes,
    isVeryEarly: isVeryEarlyCheckout || isOvernightCheckout,
  });

  // Transit to airport/station
  const transitStartMinutes = checkoutMinutes + 30;
  const transitEmoji = transitMode === 'flight' ? '✈️' : isIsland ? '✈️' : '🚗';

  items.push({
    type: 'airport-transit',
    time: minutesToTimeStr(transitStartMinutes),
    activity: `${transitEmoji} Transit to ${departureAirportCode || 'Airport'}`,
    description: isIsland
      ? `Flight to ${departureAirportCode || 'airport'} - book connecting flight in advance`
      : `Arrive 2 hours before flight`,
    duration: `${Math.round(transitDuration)} minutes`,
    location: departureAirportCode ? `${departureAirportCode} Airport` : 'Airport',
    sortTime: transitStartMinutes,
  });

  // Departure card — dark navy card marking the flight
  const arriveAirportMinutes = transitStartMinutes + transitDuration;
  items.push({
    type: 'departure',
    time: minutesToTimeStr(departMinutes),
    activity: `Departure from ${departureAirportCode || currentCity || 'Airport'}`,
    description: `Flight departs at ${minutesToTimeStr(departMinutes)}`,
    duration: '',
    location: departureAirportCode ? `${departureAirportCode} Airport` : 'Airport',
    flightRoute: departureAirportCode ? `From ${departureAirportCode}` : '',
    sortTime: departMinutes,
  });

  return items;
}

// ─── P3: Transit Dedup + Free Time ─────────────────────────────────────────

/**
 * Deduplicate transit cards with same from/to segment.
 *
 * @param {Array} items - All timeline items
 * @returns {Array} Deduped items
 */
export function deduplicateTransitItems(items) {
  if (!Array.isArray(items)) return items || [];

  const transitTypes = new Set(['transit', 'airport-transit', 'station-transit', 'port-transit', 'inter-city-travel']);
  const seen = new Set();

  return items.filter((item, idx) => {
    const isTransit = transitTypes.has(item.type);
    if (!isTransit) return true;

    // Dedup by from→to key
    const from = (item.from?.city || item.from || '').toString().toLowerCase();
    const to = (item.to?.city || item.to || '').toString().toLowerCase();
    const key = `${from}→${to}`;
    if (seen.has(key)) return false;
    seen.add(key);

    // Suppress consecutive transit cards (keep the first one)
    if (idx > 0) {
      const prev = items[idx - 1];
      if (transitTypes.has(prev?.type)) return false;
    }

    return true;
  });
}

/**
 * Cap free time blocks at a maximum duration and suppress near travel items.
 *
 * @param {Array} items - All timeline items
 * @param {Object} context
 * @param {number} [context.maxFreeMinutes=120] - Max free time in minutes (2 hours)
 * @param {boolean} [context.isDepartureDay] - Suppress free time on departure day
 * @returns {Array} Items with free time capped/suppressed
 */
export function capFreeTime(items, context = {}) {
  if (!Array.isArray(items)) return items || [];

  const maxMinutes = context.maxFreeMinutes || 120; // D1: 480→120

  return items.filter(item => {
    if (item.type !== 'free-time' && item.type !== 'free_time') return true;

    // Suppress on departure day
    if (context.isDepartureDay) return false;

    // Parse duration
    const durationStr = item.duration || '';
    const hourMatch = durationStr.match(/(\d+\.?\d*)\s*(?:h|hour)/i);
    const minMatch = durationStr.match(/(\d+)\s*(?:m|min)/i);
    let durationMin = 0;
    if (hourMatch) durationMin += parseFloat(hourMatch[1]) * 60;
    if (minMatch) durationMin += parseInt(minMatch[1]);

    // D1: Remove entirely if > 4 hours (unrealistic gap)
    if (durationMin > 240) return false;

    // Cap at max
    if (durationMin > maxMinutes) {
      const hours = Math.floor(maxMinutes / 60);
      const mins = maxMinutes % 60;
      item.duration = mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
    }

    return true;
  });
}

// ─── D2: Breakfast Hotel Context ─────────────────────────────────────────────

/**
 * On inter-city travel days, user wakes up at previous day's hotel.
 * Returns the correct hotel context for breakfast injection.
 *
 * @param {Object} context
 * @param {boolean} context.isInterCityTravelDay
 * @param {string} [context.prevHotelName] - Previous day's hotel name
 * @param {string} [context.hotelName] - Current day's (arrival city) hotel name
 * @param {string} [context.prevCity] - Previous day's city
 * @param {string} [context.currentCity] - Current day's city
 * @returns {{ hotel: string, city: string }}
 */
export function getBreakfastHotelContext(context) {
  if (context.isInterCityTravelDay && context.prevHotelName) {
    return { hotel: context.prevHotelName, city: context.prevCity || context.currentCity };
  }
  return { hotel: context.hotelName || 'Hotel', city: context.currentCity };
}

// ─── D3: Meal Blocked by Transport ──────────────────────────────────────────

/**
 * Check if a meal window is blocked by an active transport leg (flight/train/bus).
 *
 * @param {string} mealType - 'breakfast' | 'lunch' | 'dinner'
 * @param {Object} context
 * @param {number} [context.transportDepartureMinutes] - Transport departure in minutes from midnight
 * @param {number} [context.transportArrivalMinutes] - Transport arrival in minutes from midnight
 * @returns {boolean} True if the meal is blocked by transport
 */
export function isMealBlockedByTransport(mealType, context) {
  if (!context.transportDepartureMinutes && context.transportDepartureMinutes !== 0) return false;
  const mealWindows = { breakfast: [420, 570], lunch: [690, 840], dinner: [1080, 1260] };
  const [start, end] = mealWindows[mealType] || [0, 0];
  const dep = context.transportDepartureMinutes;
  const arr = context.transportArrivalMinutes || dep + 120; // Default 2hr transport

  // Fix 3: For breakfast, only block if departure is before 8AM (480).
  // User can eat early breakfast (7-8AM) before a 9AM+ flight.
  // The old logic (dep < end && arr > start) was too aggressive — a 9AM flight
  // (dep=540) triggered 540 < 570, blocking breakfast entirely.
  if (mealType === 'breakfast') {
    return dep < 480; // Departure before 8AM = no time for breakfast
  }

  // For lunch/dinner: blocked if transport overlaps the meal window
  return dep < end && arr > start;
}

// ─── D4: Logistics Sort Priority ────────────────────────────────────────────

/**
 * Priority ordering for logistics items when times are close/identical.
 * Lower number = earlier in the day.
 *
 * @param {string} type - Logistics item type
 * @returns {number} Sort priority (lower = first)
 */
// Chronological order of logistics items within a single day:
//   checkout (leaving hotel)
//     → airport-transit / station-transit / port-transit (going TO airport)
//     → airport-arrival / arrival-customs (at airport: check-in on the
//        depart day, customs on the arrival day)
//     → inter-city-travel / long-distance-transfer (the flight/drive itself)
//     → transit / transit-arrival (leaving airport FOR hotel on arrival day)
//     → hotel-checkin
//
// T6 fix: previously airport-arrival was priority 1 and airport-transit was
// priority 3, so within the 30-min tiebreak window the comparator would
// reorder "Check-in at airport" BEFORE "Transit to airport" on the
// departure day. The real-world sequence is transit → arrive → check-in,
// so airport-transit must sort before airport-arrival.
const LOGISTICS_PRIORITY = {
  'hotel-checkout': 0, 'checkout': 0,
  'airport-transit': 1, 'station-transit': 1, 'port-transit': 1,
  'airport-arrival': 2, 'arrival-customs': 2, 'arrival-domestic': 2,
  'inter-city-travel': 3, 'long-distance-transfer': 3,
  'transit': 4, 'transit-arrival': 4,
  'hotel-checkin': 5, 'checkin': 5,
};
export function logisticsSortPriority(type) {
  return LOGISTICS_PRIORITY[type] ?? 10;
}

// ─── D5: Overnight Arrival Day Cleanup ──────────────────────────────────────

/**
 * Clean up an overnight arrival day by stripping AI departure logistics,
 * pre-11 AM items (except settle-in types), and post-9 PM items.
 *
 * @param {Array} schedule - Day's schedule array from AI
 * @param {Array} meals - Day's meals array from AI
 * @param {Object} context
 * @param {boolean} context.isOvernightArrival - Is this an overnight arrival day?
 * @returns {{ schedule: Array, meals: Array }}
 */
export function cleanOvernightArrivalDay(schedule, meals, context) {
  if (!context.isOvernightArrival) return { schedule, meals };

  const DEPARTURE_PATTERNS = ['checkout', 'check out', 'check-out', 'transit to airport',
    'head to airport', 'flight to', 'overnight journey', 'head to station',
    'depart for', 'fly to', 'travel to airport'];
  const DEPARTURE_TYPES = ['checkout', 'hotel-checkout', 'inter-city-travel', 'airport-transit'];
  // ALLOWED_EARLY was used here to preserve settle-in / arrival / recovery
  // items emitted by the LLM. Removed intentionally — the shared arrival
  // injection in createDayTimeline.js is now the single source of truth for
  // those cards, and LLM copies cause duplicate/out-of-order rows.
  // Items we strip *even when the LLM places them earliest* — the shared
  // createDayTimeline logic (lines 970+) injects canonical arrival, customs,
  // transit, and check-in cards, so any LLM-emitted duplicates just produce
  // duplicate rows and out-of-order sorting. "Free Time" before 11 AM on an
  // overnight arrival day is also wrong — the user is in transit/recovery.
  //
  // Bug #6 (Kyoto → Positano day 4 showed "customs → free time → arrival →
  // hotel check in"): LLM emitted its own arrival + customs items with
  // different times from the shared injection, and free-time filled the
  // gap between them. After this dedup, only the injected sequence
  // remains.
  const ARRIVAL_DUPLICATE_PATTERNS = [
    'arrival at', 'arrival &', 'arrival/', 'welcome to', 'land at', 'landing at',
    'customs', 'immigration', 'border control', 'passport control',
    'transit to hotel', 'transfer to hotel', 'taxi to hotel',
    'hotel check-in', 'hotel checkin', 'check in to',
  ];
  const ARRIVAL_DUPLICATE_TYPES = [
    'overnight-arrival', 'arrival-customs', 'arrival-domestic', 'arrival-baggage',
    'airport-transit', 'station-transit', 'hotel-checkin', 'recovery-note',
    'free-time',
  ];

  // 1. Strip departure logistics from AI schedule
  const cleaned = (schedule || []).filter(item => {
    const name = (item.activity || item.description || '').toLowerCase();
    const type = (item.type || '').toLowerCase();
    if (DEPARTURE_PATTERNS.some(p => name.includes(p))) return false;
    if (DEPARTURE_TYPES.includes(type)) return false;
    // Strip items the shared injection will replace (prevents the Kyoto →
    // Positano "customs → free time → arrival → hotel check in" bug).
    if (ARRIVAL_DUPLICATE_TYPES.includes(type)) return false;
    if (ARRIVAL_DUPLICATE_PATTERNS.some(p => name.includes(p))) return false;
    return true;
  });

  // 2. Remove items before 11 AM, full stop. Previously this allowed
  // "settle/check-in/arrival" prefixes, but the shared layer owns those
  // exclusively now, so the filter can just hard-cap 11 AM.
  const timeGated = cleaned.filter(item => {
    const mins = parseTimeToMinutes(item.time);
    if (mins !== null && mins !== undefined && mins < 660) {
      return false;
    }
    return true;
  });

  // 3. Cap at 9 PM (except hotel-return)
  const capped = timeGated.filter(item => {
    const mins = parseTimeToMinutes(item.time);
    if (mins !== null && mins !== undefined && mins > 1260) {
      const type = (item.type || '').toLowerCase();
      return type.includes('hotel-return') || type.includes('return');
    }
    return true;
  });

  // 4. Filter meals the same way (no meals before 11 AM unless breakfast-type)
  const cleanedMeals = (meals || []).filter(meal => {
    const mins = parseTimeToMinutes(meal.time);
    if (mins !== null && mins !== undefined && mins < 660) {
      return (meal.meal || '').toLowerCase() === 'breakfast';
    }
    return true;
  });

  return { schedule: capped, meals: cleanedMeals };
}

// ─── Fix 4: Collision-Aware Meal Slot Finder ─────────────────────────────────

/**
 * Find a collision-free time slot for an injected meal within its valid range.
 * Returns the best time in minutes, or null if no slot is available.
 *
 * @param {string} mealType - 'breakfast' | 'lunch' | 'dinner'
 * @param {Array} existingItems - Items already in the timeline (must have .sortTime and .duration)
 * @param {number} defaultTime - Preferred time in minutes from midnight
 * @returns {number|null} Time slot in minutes, or null if no slot found
 */
export function findMealSlot(mealType, existingItems, defaultTime, opts = {}) {
  const MEAL_DURATIONS = { breakfast: 45, lunch: 60, dinner: 90 };
  // Expanded ranges — dinner especially needs a wider window (6PM–10PM instead of 6PM–9:30PM)
  const MEAL_RANGES = { breakfast: [420, 570], lunch: [690, 930], dinner: [1050, 1320] };
  const duration = MEAL_DURATIONS[mealType] || 60;
  const [rangeStart, rangeEnd] = MEAL_RANGES[mealType] || [0, 1440];

  // Check if a time slot has a conflict with existing items
  const hasConflict = (time) => existingItems.some(item => {
    if (!item.sortTime && item.sortTime !== 0) return false;
    const itemDuration = parseDurationToMinutes(item.duration) || 60;
    // Two intervals overlap if: start_A < end_B AND start_B < end_A
    return item.sortTime < time + duration && time < item.sortTime + itemDuration;
  });

  // Try default time first
  if (!hasConflict(defaultTime)) return defaultTime;

  // Try slots in 15-min increments within the meal's valid range
  for (let t = rangeStart; t <= rangeEnd - duration; t += 15) {
    if (!hasConflict(t)) return t;
  }

  // Fallback: try a wider extended range (+/- 1 hour beyond normal range)
  // This ensures meals always get placed rather than silently skipped
  const extStart = Math.max(360, rangeStart - 60); // Don't go before 6:00 AM
  const extEnd = Math.min(1380, rangeEnd + 60);    // Don't go past 11:00 PM
  for (let t = extStart; t <= extEnd - duration; t += 15) {
    if (t >= rangeStart && t <= rangeEnd) continue; // Already tried these
    if (!hasConflict(t)) return t;
  }

  // Last-resort: if `force: true` is set (default for required meals), return
  // `defaultTime` even if it collides. Caller shows a "pick a spot" card; the
  // user can reschedule. Previous behavior (return null → drop meal silently)
  // left holes in the day — worse UX than a collision the user can resolve.
  if (opts.force) return defaultTime;
  return null;
}

// ─── Fix 7: Shared Overnight Transport Detection ─────────────────────────────

/**
 * Determine if a transport leg is an overnight journey using multiple heuristics.
 * Used by both web and mobile for consistent overnight detection.
 * Replaces web's local `checkOvernightDeparture()` and mobile's regex-only check.
 *
 * Heuristics (in order):
 * 1. Explicit flags: isOvernight, arrivalDayOffset > 0
 * 2. Text-based: overnight, red-eye, next day, sleeper keywords
 * 3. Time-based: late departure (≥8PM), long-haul (≥10h), evening+duration combos, midnight crossing
 * 4. Cross-continental: afternoon departure between different continents
 *
 * @param {Object} leg - Transport leg data (from interCityTransport or flights)
 * @param {Object} [opts] - Optional: { dynamicGeographyCache, calculateFlightArrival }
 *   - calculateFlightArrival: If provided, used to verify timezone-adjusted arrival day
 *     for long-haul flights. This catches eastbound flights (e.g. HK→NYC) where the
 *     timezone shift means the flight arrives same-day despite 10+ hour duration.
 * @returns {boolean} True if the leg is an overnight journey
 */
export function checkOvernightTransport(leg, opts = {}) {
  if (!leg) return false;

  // 1. Explicit flags
  if (leg.isOvernight) return true;
  if (leg.arrivalDayOffset && leg.arrivalDayOffset > 0) return true;

  // 2. Text-based (keeps mobile's existing regex coverage)
  const notes = (leg.notes || leg.description || '').toLowerCase();
  if (/overnight|red.?eye|next.?day|sleeper/.test(notes)) return true;

  // 3. Time-based heuristics
  const depHour = leg.departureTime ? parseInt(leg.departureTime.split(':')[0]) : -1;
  const durationMatch = (leg.duration || '').match(/(\d+)h/);
  const hours = durationMatch ? parseInt(durationMatch[1]) : 0;

  if (depHour >= 20) return true;                                        // Any mode departing after 8PM

  // For 10+ hour flights: check if timezone shift makes it a same-day arrival
  // (e.g. HK→NYC: 16h flight, -13h timezone = arrives same day at 1 PM)
  if (hours >= 10) {
    if (opts.calculateFlightArrival && leg.departureTime && depHour >= 0) {
      const fromCity = leg?.from?.city || '';
      const toCity = leg?.to?.city || '';
      if (fromCity && toCity) {
        let durHours = hours;
        const minMatch = (leg.duration || '').match(/(\d+)\s*m/);
        if (minMatch) durHours += parseInt(minMatch[1]) / 60;
        const result = opts.calculateFlightArrival(leg.departureTime, durHours, fromCity, toCity);
        if (result.dayOffset <= 0) {
          // Timezone adjustment means same-day arrival — NOT overnight
          return false;
        }
      }
    }
    return true;                                                         // Any 10h+ flight (catches Santorini→Tokyo 9AM, 14h30m)
  }

  if (hours >= 8 && depHour >= 18) return true;                          // 8h+ evening departure
  if (depHour >= 0 && hours > 0 && (depHour + hours) >= 24) return true; // Crosses midnight

  // 4. Cross-continental afternoon departure
  const fromCity = leg?.from?.city || '';
  const toCity = leg?.to?.city || '';
  if (fromCity && toCity) {
    const cache = opts.dynamicGeographyCache || {};
    const fromCountry = getCityCountrySync(fromCity, cache);
    const toCountry = getCityCountrySync(toCity, cache);
    const fromCont = getContinentForCountry(fromCountry, cache);
    const toCont = getContinentForCountry(toCountry, cache);
    if (fromCont && toCont && fromCont !== toCont && depHour >= 14) return true;
  }

  return false;
}

// ─── Fix 8: Transport Minutes Parser ──────────────────────────────────────────

/**
 * Parse departure and arrival minutes from interCityTransport data.
 * Shared between web and mobile to avoid drift.
 *
 * @param {Array} interCityTransport - Array of transport leg objects
 * @returns {{ departureMinutes: number|undefined, arrivalMinutes: number|undefined }}
 */
export function parseTransportMinutes(interCityTransport) {
  if (!interCityTransport?.length) return { departureMinutes: undefined, arrivalMinutes: undefined };
  const transport = interCityTransport[0];
  let departureMinutes, arrivalMinutes;

  if (transport.departureTime) {
    const parts = transport.departureTime.split(':').map(Number);
    departureMinutes = (parts[0] || 0) * 60 + (parts[1] || 0);
  }
  if (transport.arrivalTime) {
    const parts = transport.arrivalTime.split(':').map(Number);
    arrivalMinutes = (parts[0] || 0) * 60 + (parts[1] || 0);
  }

  // If arrival < departure, flight is overnight — set to end of day for blocking purposes
  if (arrivalMinutes !== undefined && departureMinutes !== undefined && arrivalMinutes < departureMinutes) {
    arrivalMinutes = 1440; // Spans full day
  }

  return { departureMinutes, arrivalMinutes };
}

// ─── Fix 6: Pre-Dawn Safety Cap ───────────────────────────────────────────────

/**
 * Remove non-logistics items scheduled before 6 AM.
 * Safety net that catches items (like "Fira Caldera Walk at 1:16 AM")
 * that slip through when overnight detection fails.
 *
 * @param {Array} items - Schedule or timeline items with .time or .sortTime
 * @returns {Array} Filtered items (pre-dawn non-logistics removed)
 */
export function filterPreDawnItems(items) {
  if (!items?.length) return items;
  const LOGISTICS_TYPES = ['logistics', 'overnight-journey', 'overnight-note',
    'hotel-checkin', 'checkin', 'arrival', 'settle-in', 'recovery-note',
    'arrival-customs', 'arrival-domestic', 'airport-transit'];

  return items.filter(item => {
    const mins = item.sortTime ?? parseTimeToMinutes(item.time);
    if (mins !== null && mins !== undefined && mins > 0 && mins < 360) {
      const type = (item.type || '').toLowerCase();
      // Keep logistics items (arrivals, check-ins, etc.) even before 6 AM
      if (LOGISTICS_TYPES.some(t => type.includes(t))) return true;
      // Remove all other items (activities, meals, free time) before 6 AM
      return false;
    }
    return true;
  });
}

// ─── Timeline Sort Comparator ──────────────────────────────────────────────

/** Type priority for timeline tiebreaking when items have the same sortTime. */
export const TIMELINE_TYPE_PRIORITY = {
  'activity': 0, 'meal': 1, 'hotel-checkin': 2, 'checkout': 2,
  'transit': 3, 'local-transit': 3, 'long-distance-departure': 3, 'free-time': 4,
};

/**
 * Sort comparator for timeline items. Sorts by sortTime, with logistics-first
 * tiebreaking within a 30-minute window, then by general type priority.
 *
 * @param {Object} a - Timeline item
 * @param {Object} b - Timeline item
 * @returns {number} Sort comparison value
 */
export function timelineSortComparator(a, b) {
  if (a.sortTime !== b.sortTime) {
    // D4: When times are within 30 min, use logistics priority as tiebreaker
    if (Math.abs(a.sortTime - b.sortTime) < 30) {
      const aPri = logisticsSortPriority(a.type);
      const bPri = logisticsSortPriority(b.type);
      if (aPri !== 10 || bPri !== 10) { // At least one is a logistics type
        if (aPri !== bPri) return aPri - bPri;
      }
    }
    return a.sortTime - b.sortTime;
  }
  // D4: Same time — use logistics priority first, then general type priority
  const aLogPri = logisticsSortPriority(a.type);
  const bLogPri = logisticsSortPriority(b.type);
  if (aLogPri !== bLogPri) return aLogPri - bLogPri;
  return (TIMELINE_TYPE_PRIORITY[a.type] ?? 99) - (TIMELINE_TYPE_PRIORITY[b.type] ?? 99);
}

// ─── City Name Normalization ──────────────────────────────────────────────

/**
 * Normalize city names for comparison by stripping country suffixes,
 * area prefixes (Historic Center, Old Town, etc.), and collapsing whitespace.
 *
 * @param {string} city - City name to normalize
 * @returns {string} Normalized city name (lowercase, trimmed)
 */
export function normalizeCityName(city) {
  if (!city) return '';
  return city.toLowerCase()
    .replace(/,\s*[a-z]+$/i, '')  // Remove country suffix: "Florence, Italy" → "Florence"
    .replace(/^(historic center|old town|city center|downtown|central)\s*,?\s*/i, '') // Remove area prefix
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Deduplicate state/country suffix in city display names.
 * "Ocean City, Maryland, Maryland" → "Ocean City, Maryland"
 * "Paris, France, France" → "Paris, France"
 * "Tokyo, Japan" → "Tokyo, Japan" (no change)
 */
export function deduplicateCitySuffix(cityName) {
  if (!cityName) return '';
  const parts = cityName.split(',').map(p => p.trim());
  if (parts.length < 2) return cityName;
  // Remove consecutive duplicate parts (case-insensitive)
  const deduped = [parts[0]];
  for (let i = 1; i < parts.length; i++) {
    if (parts[i].toLowerCase() !== parts[i - 1].toLowerCase()) {
      deduped.push(parts[i]);
    }
  }
  return deduped.join(', ');
}

// ─── Explicit Overnight Arrival Check ─────────────────────────────────────

/**
 * Strict overnight check — only returns true for EXPLICIT overnight markers
 * (isOvernight flag or arrivalDayOffset > 0). Does NOT use heuristics.
 * Use this when you need to avoid false positives from heuristic-based detection.
 *
 * @param {Object} leg - Transport leg data
 * @returns {boolean} True only if explicit overnight flags are set
 */
export function isExplicitOvernightArrival(leg) {
  if (!leg) return false;
  if (leg.isOvernight) return true;
  if (leg.arrivalDayOffset && leg.arrivalDayOffset > 0) return true;
  return false;
}

// ─── Meal Coverage Helpers ────────────────────────────────────────────────

/**
 * Check if a timeline already contains a meal of the given type.
 * Searches multiple fields: meal, activity, mealType, originalType, restaurant.
 * Handles both pre-timeline (LLM output) and post-timeline (type overwritten to 'meal') formats.
 *
 * @param {Array} items - Timeline items to search
 * @param {string} type - Meal type: 'breakfast', 'lunch', or 'dinner'
 * @returns {boolean} True if the meal type is already present
 */
export function hasMealType(items, type) {
  if (!items?.length || !type) return false;
  const t = type.toLowerCase();
  return items.some(item => {
    // Check if item is meal-like (type === 'meal' or type matches meal type directly)
    const isMealItem = item.type === 'meal' || item.type === t;
    const activityMatch = (item.activity || '').toLowerCase().includes(t);

    if (!isMealItem && !activityMatch) return false;

    // Check all possible fields where the meal type might be stored
    return (item.meal || '').toLowerCase().includes(t) ||
           (item.mealType || '').toLowerCase().includes(t) ||
           (item.originalType || '').toLowerCase().includes(t) ||
           activityMatch ||
           // Direct type match (before timeline overwrites type to 'meal')
           item.type === t ||
           // Restaurant field sometimes contains meal type (e.g. "Breakfast at Hotel")
           (item.restaurant || '').toLowerCase().startsWith(t);
  });
}

// Cruise/expedition ship name keywords — these destinations have all meals
// on the ship (no shore-based restaurants at the port of call).
// Triggered when city looks like Antarctica/Arctic/Galapagos AND hotel name
// contains one of these words, OR when any of these patterns is present.
// Exported so createDayTimeline can also use these to swap "Hotel Check-in"
// labels to "Board Ship" / "Return to Ship" on cruise trips.
export const CRUISE_SHIP_NAME_KEYWORDS = [
  'adventurer', 'expedition', 'explorer', 'voyager', 'navigator',
  'endeavour', 'endeavor', 'seabourn', 'silver', 'ponant', 'hurtigruten',
  'national geographic', 'lindblad', 'aurora', 'ocean nova', 'ushuaia',
  'plancius', 'ortelius', 'polar', 'viking ocean', 'viking star',
  'scenic eclipse', 'world explorer', 'le boreal', 'le lyrial',
];

// Destinations with no shore-based tourist dining — all meals on expedition
// ship / lodge. "Lunch in Antarctica" is meaningless because there are no
// restaurants in Antarctica (just research stations).
const CRUISE_ONLY_DESTINATION_KEYWORDS = [
  'antarctica', 'antarctic peninsula', 'ross sea', 'mcmurdo',
  'weddell sea', 'south shetland', 'south georgia',
  'svalbard', 'north pole', 'arctic circle',
  'galapagos',
];

/**
 * Check if a destination is only accessible via cruise/expedition ship.
 * All meals are on the ship (no shore-based tourist restaurants).
 * @param {string} city
 * @returns {boolean}
 */
export function isCruiseOnlyDestination(city) {
  if (!city) return false;
  const lower = String(city).toLowerCase().trim();
  return CRUISE_ONLY_DESTINATION_KEYWORDS.some(kw => lower.includes(kw));
}

/**
 * Check if the accommodation is a resort, all-inclusive, or cruise.
 * Used to determine if meals should be labeled with the hotel restaurant.
 *
 * @param {string} hotelName - Hotel/accommodation name
 * @param {string} [accommodationType] - Accommodation type (e.g., 'cruise')
 * @param {string} [city] - Current city; used to detect cruise-only destinations
 *   where all meals are on the ship (Antarctica, Galapagos, Svalbard, etc.)
 * @returns {boolean} True if resort/all-inclusive/cruise
 */
// Luxury resort brand keywords — properties whose names do NOT contain the
// literal word "resort" but whose entire business model is on-property
// dining/spa/excursions, so missing meals should resolve to the resort's
// own restaurants rather than firing the "pick a spot" placeholder.
//
// Surfaced from a Vietnam Six Senses Ninh Van Bay UAT — the Day 7 dinner
// rendered as "Dinner · pick a spot" because "Six Senses Ninh Van Bay"
// doesn't contain "resort", so isResortStay() returned false. Same class
// of false negative would hit Aman, Bulgari, Banyan Tree, Soneva, etc.
//
// List is curated, not exhaustive — additions welcome. Intentionally
// excludes generic terms like "lodge" or "villa" that would over-match.
const RESORT_BRAND_KEYWORDS = [
  'six senses', 'aman ', 'amanjiwo', 'amankora', 'amangiri', 'amanwana',
  'amanyara', 'amankila', 'amanpuri', 'amanusa', 'amanjena', 'amangani',
  'amanyangyun', 'amanoi',
  'banyan tree', 'bulgari hotel', 'bulgari resort',
  'oberoi ', 'taj exotica', 'taj falaknuma', 'taj lake palace',
  'four seasons', 'one&only', 'one and only',
  'soneva fushi', 'soneva jani', 'soneva kiri',
  'rosewood ', 'park hyatt', 'andaz ', 'st regis', 'st. regis',
  'ritz-carlton', 'ritz carlton',
  'mandarin oriental', 'cheval blanc', 'belmond ',
  'jumeirah ', 'sandals ', 'beaches negril', 'beaches turks',
  'auberge ', 'edition hotel', 'capella ', 'raffles ',
  'shangri-la', 'shangri la',
  'conrad ', 'grand hyatt', 'waldorf astoria', 'kempinski',
  'nihi sumba', 'gili lankanfushi', 'velaa private',
  'cheval blanc randheli', 'el silencio',
  'fairmont ', 'le bristol', 'la mamounia', 'royal mansour',
  // Maldives + Indian Ocean luxury (heavily over-water-villa-driven)
  'jumeirah maldives', 'milaidhoo', 'cheval blanc maldives',
  'reethi rah', 'huvafen fushi', 'baros maldives', 'kanuhura',
  'naladhu', 'finolhu',
];

export function isResortStay(hotelName, accommodationType, city, stopMeta = {}) {
  // Trip-builder explicit signal trumps name-parsing heuristics. If the
  // user marked this stop as hotel-mode in the trip builder (the "Stay at
  // Hotel/Resort" path), respect that — they've told us the property is
  // the destination. Falls back to name/accommodation/city heuristics
  // when stopMeta isn't passed (legacy callers).
  if (stopMeta?.stopType === 'hotel' || stopMeta?.stopType === 'resort') return true;
  if (stopMeta?.stayOnProperty === true) return true;

  if (!hotelName || hotelName === 'Hotel') {
    // Even without a hotel name, treat cruise-only destinations as resort-style
    // so we don't emit "Lunch in Antarctica" placeholders. The LLM will fill
    // the specific ship dining name.
    if (isCruiseOnlyDestination(city)) return true;
    return false;
  }
  const lower = hotelName.toLowerCase();
  if (lower.includes('resort') ||
      lower.includes('all-inclusive') ||
      lower.includes('all inclusive') ||
      accommodationType === 'cruise') {
    return true;
  }
  // Luxury resort brand catch — covers Six Senses, Aman, Bulgari, Soneva,
  // Four Seasons, etc. that don't ship with "resort" in the name.
  if (RESORT_BRAND_KEYWORDS.some(brand => lower.includes(brand))) return true;
  // Detect expedition/cruise ship names — common in Antarctic, Arctic, and
  // Galapagos trips where the "hotel" is actually the ship.
  if (CRUISE_SHIP_NAME_KEYWORDS.some(kw => lower.includes(kw))) {
    return true;
  }
  // Cruise-only destination — fall back to resort semantics even if hotel
  // name doesn't obviously look like a ship.
  if (isCruiseOnlyDestination(city)) return true;
  return false;
}

// ─── Pace Thresholds ──────────────────────────────────────────────────────

/** Activity count thresholds by pace preference. */
export const PACE_THRESHOLDS = {
  // 'easy' is the new bottom tier — 1-2 activities + meals.
  // Hope's feedback: "Maybe make 'activities' more clear, or create a bottom
  // tier (e.g. 1 activity a day)". Counts include meals so 3/2 means at most
  // 1 real activity on a normal day, 0 on a travel day.
  easy: { normal: 3, travel: 2 },
  relaxed: { normal: 5, travel: 3 },
  moderate: { normal: 7, travel: 4 },
  balanced: { normal: 7, travel: 4 },
  packed: { normal: 9, travel: 6 },
  fast: { normal: 8, travel: 5 },
  'high-energy': { normal: 9, travel: 6 },
};

/**
 * Check if a day has too many activities based on pace preference.
 *
 * @param {Object} day - Day data with schedule and meals arrays
 * @param {Object} context
 * @param {boolean} context.isTravelDay - Is this a travel day (first day, inter-city)?
 * @param {string} [context.pace='moderate'] - User's preferred pace
 * @returns {boolean} True if the day exceeds the pace threshold
 */
export function isDayTooPacked(day, context = {}) {
  // Exclude logistics items (arrival, transit, hotel check-in/out) from the count —
  // these are mandatory time sinks, not optional activities the user can cut.
  const LOGISTICS_TYPES = new Set(['arrival-customs', 'arrival-domestic', 'arrival-baggage', 'airport-transit', 'hotel-checkin', 'checkout', 'hotel-return', 'recovery-note', 'departure']);
  const LOGISTICS_RX = /^(arrival\s+(at|in)|drive\s+to\s|taxi\s+to|transfer\s+to|transit\s+to|hotel\s+check.?in|hotel\s+check.?out|airport|immigration|customs)/i;

  const scheduleCount = (day.schedule || []).filter(s => {
    if (LOGISTICS_TYPES.has(s.type)) return false;
    if (s._isInjected) return false;
    const name = (s.activity || s.name || '').trim();
    if (LOGISTICS_RX.test(name)) return false;
    return true;
  }).length;
  const mealCount = (day.meals?.length || 0);
  const activityCount = scheduleCount + mealCount;

  const pace = (context.pace || 'moderate').toLowerCase();
  const threshold = PACE_THRESHOLDS[pace] || PACE_THRESHOLDS.moderate;
  return activityCount >= (context.isTravelDay ? threshold.travel : threshold.normal);
}

// ─── Meal Injection ───────────────────────────────────────────────────────

/**
 * Inject missing meals (breakfast, lunch, dinner) into a timeline.
 * Uses collision-aware slot finding to avoid overlapping with existing items.
 *
 * @param {Array} timeline - Existing timeline items (will be mutated)
 * @param {Object} context
 * @param {boolean} context.isFirstDay
 * @param {boolean} context.isLastDay
 * @param {boolean} context.isInterCityTravelDay
 * @param {boolean} context.isArrivalDayAfterOvernightTravel
 * @param {string} context.dayCity - Current day's base city
 * @param {string} context.dayHotel - Current day's hotel name
 * @param {string} [context.accommodationType] - 'cruise', 'hotel', etc.
 * @param {string} [context.prevHotelName] - Previous day's hotel (for breakfast context)
 * @param {string} [context.prevCity] - Previous day's city
 * @param {number} [context.transportDepartureMinutes] - For meal blocking
 * @param {number} [context.transportArrivalMinutes] - For meal blocking
 * @param {number} [context.arrivalSortTime=0] - Arrival time on arrival days
 * @param {boolean} [context.isArrivalDay=false] - Is this an arrival day?
 * @returns {Array} The mutated timeline with injected meals
 */
export function injectMissingMeals(timeline, context = {}) {
  if (!timeline) return timeline;

  const {
    isFirstDay, isLastDay, isInterCityTravelDay,
    isArrivalDayAfterOvernightTravel,
    dayCity = '', dayHotel = 'Hotel', accommodationType,
    prevHotelName, prevCity,
    transportDepartureMinutes, transportArrivalMinutes,
    arrivalSortTime = 0, isArrivalDay = false,
  } = context;

  // Skip ALL meal injection on overnight arrival days
  if (isArrivalDayAfterOvernightTravel) return timeline;

  const hasVeryEarlyDeparture = timeline.some(item =>
    (item.type === 'departure' || item.type === 'transit-departure') && item.sortTime < 420
  );
  const hasLateDeparture = timeline.some(item =>
    (item.type === 'departure' || item.type === 'transit-departure') && item.sortTime > 1200
  );

  // Pass dayCity so cruise-only destinations (Antarctica, Galapagos, Svalbard)
  // fall back to resort-style meal naming ("{ship name} Dining Room") instead
  // of placeholders like "Lunch in Antarctica".
  const resort = isResortStay(dayHotel, accommodationType, dayCity);
  // Safety: if resort=true but dayHotel is null/empty, avoid "null Restaurant".
  // Use "Ship Dining" for cruise-only destinations, else the generic "Hotel".
  const resortRestaurantLabel = dayHotel && dayHotel !== 'Hotel'
    ? `${dayHotel} Restaurant`
    : (isCruiseOnlyDestination(dayCity) ? 'Ship Dining' : 'Hotel Dining');
  const resortLocationLabel = dayHotel && dayHotel !== 'Hotel'
    ? dayHotel
    : (isCruiseOnlyDestination(dayCity) ? 'Ship' : (dayCity || 'Hotel'));
  const resortMealHint = dayHotel && dayHotel !== 'Hotel'
    ? `at ${dayHotel}`
    : (isCruiseOnlyDestination(dayCity) ? 'on the ship' : 'at the hotel');

  const mealTransportCtx = {
    transportDepartureMinutes,
    transportArrivalMinutes,
  };

  // D2: On travel days, breakfast uses previous day's hotel
  const breakfastCtx = getBreakfastHotelContext({
    isInterCityTravelDay: isInterCityTravelDay && !isFirstDay,
    prevHotelName,
    hotelName: dayHotel,
    prevCity,
    currentCity: dayCity,
  });
  const breakfastHotel = breakfastCtx.hotel;
  const breakfastCity = breakfastCtx.city;

  // --- Breakfast ---
  if (!hasMealType(timeline, 'breakfast') && !hasVeryEarlyDeparture && !isFirstDay) {
    const breakfastBlocked = isInterCityTravelDay && isMealBlockedByTransport('breakfast', mealTransportCtx);
    if (!breakfastBlocked) {
      // Cruise destinations: breakfast is also on the ship. Use the resort-style
      // label so we don't emit "Breakfast in Antarctica" when the ship has dining.
      const breakfastIsOnShip = resort && isCruiseOnlyDestination(breakfastCity || dayCity);
      const breakfastHasRealHotel = breakfastHotel && breakfastHotel !== 'Hotel';
      timeline.push({
        type: 'meal', meal: 'Breakfast', mealType: 'breakfast', originalType: 'breakfast',
        time: '08:30', activity: 'Breakfast',
        restaurant: breakfastHasRealHotel
          ? `${breakfastHotel} Restaurant`
          : (breakfastIsOnShip ? resortRestaurantLabel : `Breakfast in ${breakfastCity || 'the area'}`),
        description: breakfastCity ? `Start the day with breakfast in ${breakfastCity}` : 'Start the day with a local breakfast',
        duration: '45 minutes',
        location: breakfastHasRealHotel ? breakfastHotel : (breakfastIsOnShip ? resortLocationLabel : breakfastCity),
        sortTime: 510,
        isDisplayInjected: true,
        _needsReplacement: !breakfastIsOnShip && !breakfastHasRealHotel,
      });
    }
  }

  // D6: Shift breakfast earlier if it overlaps with next timeline item
  const injectedBreakfast = timeline.find(i => i.meal === 'Breakfast' && i.sortTime === 510);
  if (injectedBreakfast) {
    const breakfastEnd = 510 + 45;
    const earliestNonMeal = timeline
      .filter(i => i.type !== 'meal' && i.sortTime > 0)
      .sort((a, b) => a.sortTime - b.sortTime)[0];
    if (earliestNonMeal && earliestNonMeal.sortTime < breakfastEnd) {
      const newBreakfastStart = Math.max(420, earliestNonMeal.sortTime - 45);
      const h = Math.floor(newBreakfastStart / 60);
      const m = newBreakfastStart % 60;
      injectedBreakfast.time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      injectedBreakfast.sortTime = newBreakfastStart;
    }
  }

  // --- Lunch ---
  if (!hasMealType(timeline, 'lunch') && !isLastDay && !(isArrivalDay && arrivalSortTime > 720)) {
    const lunchBlocked = isInterCityTravelDay && isMealBlockedByTransport('lunch', mealTransportCtx);
    if (!lunchBlocked) {
      const defaultLunchTime = isArrivalDay ? Math.max(780, arrivalSortTime + 60) : 780;
      // force:true — always show a lunch card, even if there's a collision.
      // Better to show "Lunch · pick a spot" the user can reschedule than a silent gap.
      const lunchSlot = findMealSlot('lunch', timeline.filter(i => i.sortTime > 0), defaultLunchTime, { force: true });
      if (lunchSlot !== null) {
        const lunchHours = Math.floor(lunchSlot / 60);
        const lunchMins = lunchSlot % 60;
        timeline.push({
          type: 'meal', meal: 'Lunch', mealType: 'lunch', originalType: 'lunch',
          time: `${String(lunchHours).padStart(2, '0')}:${String(lunchMins).padStart(2, '0')}`,
          activity: 'Lunch',
          restaurant: resort ? resortRestaurantLabel : (dayCity ? `Lunch in ${dayCity}` : 'Local Lunch'),
          description: resort ? `Lunch ${resortMealHint}` : (dayCity ? `Enjoy lunch in ${dayCity}` : 'Enjoy a local lunch'),
          duration: '1 hour', location: resort ? resortLocationLabel : dayCity, sortTime: lunchSlot,
          isDisplayInjected: true, _needsReplacement: !resort,
        });
      }
    }
  }

  // --- Dinner ---
  if (!hasMealType(timeline, 'dinner') && !hasLateDeparture && !isLastDay) {
    const dinnerBlocked = isInterCityTravelDay && isMealBlockedByTransport('dinner', mealTransportCtx);
    if (!dinnerBlocked) {
      // force:true — dinner is the most commonly-dropped meal when the day is
      // tight. Always show a card the user can tap/reschedule; don't silently drop.
      const dinnerSlot = findMealSlot('dinner', timeline.filter(i => i.sortTime > 0), 1170, { force: true });
      if (dinnerSlot !== null) {
        const dinnerHours = Math.floor(dinnerSlot / 60);
        const dinnerMins = dinnerSlot % 60;
        timeline.push({
          type: 'meal', meal: 'Dinner', mealType: 'dinner', originalType: 'dinner',
          time: `${String(dinnerHours).padStart(2, '0')}:${String(dinnerMins).padStart(2, '0')}`,
          activity: 'Dinner',
          restaurant: resort ? resortRestaurantLabel : '{TBD: pick a dinner spot}',
          description: resort ? `Dinner ${resortMealHint}` : '',
          duration: '1 hour 30 minutes', location: resort ? resortLocationLabel : dayCity, sortTime: dinnerSlot,
          isDisplayInjected: true, _needsReplacement: !resort,
        });
      }
    }
  }

  return timeline;
}

// ─── Orchestrator ──────────────────────────────────────────────────────────

/**
 * Main entry point: process a single day's data for display.
 * Applies all guardrails in the correct order.
 *
 * @param {Object} dayData - AI-generated day data
 * @param {Object} context - Full context for all guardrails
 * @param {number} context.dayIndex - 0-based day index
 * @param {number} context.totalDays - Total number of days
 * @param {boolean} [context.isInterCityTravelDay]
 * @param {boolean} [context.isOvernightFlight]
 * @param {boolean} [context.isOvernightTrain]
 * @param {boolean} [context.isDomestic]
 * @param {string} [context.prevCity]
 * @param {string} [context.currentCity]
 * @param {string} [context.arrivalAirportCode]
 * @param {string} [context.arrivalAirportName]
 * @param {string} [context.homeAirportCode]
 * @param {string} [context.departureAirportCode]
 * @param {string} [context.arrivalTime]
 * @param {string} [context.departureTime]
 * @param {string} [context.hotelName]
 * @param {string} [context.destinationName]
 * @param {number} [context.transitDurationMinutes]
 * @returns {Object} Processed day data with injectedItems array
 */
export function processDayForDisplay(dayData, context = {}) {
  if (!dayData) return { ...dayData, injectedItems: [] };

  const isFirstDay = context.dayIndex === 0;
  const isLastDay = context.dayIndex === context.totalDays - 1;
  const isTravelDay = isFirstDay || context.isInterCityTravelDay;
  const currentCity = sanitizeBaseCity(context.currentCity || dayData.baseCity || '', context.currentCity || '');

  // 0. D5: Clean overnight arrival day (strip AI departure logistics, pre-11 AM, post-9 PM)
  if (context.isOvernightFlight || context.isOvernightArrival) {
    const cleaned = cleanOvernightArrivalDay(dayData.schedule, dayData.meals, {
      isOvernightArrival: true,
    });
    dayData.schedule = cleaned.schedule;
    dayData.meals = cleaned.meals;
  }

  // 1. Filter duplicate logistics
  if (dayData.schedule) {
    dayData.schedule = filterDuplicateLogisticsItems(dayData.schedule, {
      isFirstDay,
      isLastDay,
      isInterCityTravelDay: context.isInterCityTravelDay,
      prevCity: context.prevCity,
      currentCity,
    });
  }

  // 1b. D3: Remove meals blocked by transport on travel days
  if (context.isInterCityTravelDay && dayData.meals && (context.transportDepartureMinutes || context.transportDepartureMinutes === 0)) {
    dayData.meals = dayData.meals.filter(meal => {
      const mealType = (meal.meal || meal.type || '').toLowerCase();
      const normalizedType = mealType.includes('breakfast') ? 'breakfast'
        : mealType.includes('lunch') ? 'lunch'
        : mealType.includes('dinner') ? 'dinner' : null;
      if (!normalizedType) return true;
      return !isMealBlockedByTransport(normalizedType, {
        transportDepartureMinutes: context.transportDepartureMinutes,
        transportArrivalMinutes: context.transportArrivalMinutes,
      });
    });
  }

  // 2. Fix AM/PM times (schedule + meals combined)
  const allItems = [...(dayData.schedule || []), ...(dayData.meals || [])];
  fixAMPMTimes(allItems);

  // 3. Cap travel day and departure day activities
  if (dayData.schedule) {
    dayData.schedule = capTravelDayActivities(dayData.schedule, {
      isTravelDay: isTravelDay || isLastDay,
      maxActivities: isLastDay ? 2 : 3,  // Departure day gets max 2 activities (need time for checkout+airport)
    });
  }

  // 4. Validate airport codes in transport items
  if (dayData.interCityTransport) {
    validateFlightAirportCodes(
      Array.isArray(dayData.interCityTransport) ? dayData.interCityTransport : [dayData.interCityTransport],
      { fromCity: context.prevCity, toCity: currentCity }
    );
  }

  // 5. Build injected items
  const injectedItems = [];

  // Day 1: Arrival cards + recovery
  if (isFirstDay) {
    const arrivalItems = createArrivalItems(dayData, {
      isDomestic: context.isDomestic,
      arrivalAirportCode: context.arrivalAirportCode,
      arrivalAirportName: context.arrivalAirportName,
      homeAirportCode: context.homeAirportCode,
      arrivalTime: context.arrivalTime,
      hotelName: context.hotelName,
      destinationName: context.destinationName,
    });
    injectedItems.push(...arrivalItems);
  }

  // Mid-trip inter-city travel day: checkout from prev hotel + arrival at new city
  if (context.isInterCityTravelDay && !isFirstDay && !isLastDay) {
    const prevHotel = context.prevHotelName || '';
    // Checkout card
    if (prevHotel) {
      const transportDepMins = context.transportDepartureMinutes;
      // Checkout 90 min before transport departure, or default 9 AM
      const checkoutMins = transportDepMins > 0 ? Math.max(transportDepMins - 90, 7 * 60) : 9 * 60;
      injectedItems.push({
        type: 'checkout',
        time: minutesToTimeStr(checkoutMins),
        activity: '🧳 Hotel Checkout',
        description: `Check out of ${prevHotel} and head to your next destination`,
        location: prevHotel,
        duration: '15 minutes',
        sortTime: checkoutMins,
      });
    }
    // Arrival cards at new city (customs + hotel check-in) — reuse createArrivalItems
    const arrivalItems = createArrivalItems(dayData, {
      isDomestic: context.isDomestic,
      arrivalAirportCode: context.arrivalAirportCode,
      arrivalAirportName: context.arrivalAirportName,
      homeAirportCode: context.departureAirportCode || context.homeAirportCode,
      arrivalTime: context.arrivalTime,
      hotelName: context.hotelName,
      destinationName: context.destinationName,
    });
    injectedItems.push(...arrivalItems);
  }

  // Recovery after overnight travel OR jet lag after long-haul same-day arrival
  // Pass extended context so createRecoveryNote can handle the jet lag case too
  {
    const recovery = createRecoveryNote(dayData, {
      isOvernightFlight: context.isOvernightFlight,
      isOvernightTrain: context.isOvernightTrain,
      hotelName: context.hotelName,
      currentCity,
      arrivalMinutes: parseTimeToMinutes(context.arrivalTime) || 600,
      isLongHaulFlight: context.isLongHaulFlight,
      arrivalDayOffset: context.arrivalDayOffset,
      timezoneDiff: context.timezoneDiff,
      travelDuration: context.travelDuration,
    });
    if (recovery) injectedItems.push(recovery);
  }

  // Last Day: Departure items
  if (isLastDay) {
    // Check if breakfast already exists
    const hasBreakfast = allItems.some(it =>
      ((it.meal || it.activity || '').toLowerCase().includes('breakfast'))
    );

    const isIsland = isIslandCity(currentCity);

    const departureItems = createDepartureItems(dayData, {
      departureTime: context.departureTime,
      hotelName: context.hotelName,
      departureAirportCode: context.departureAirportCode,
      currentCity,
      transitDurationMinutes: context.transitDurationMinutes,
      isIsland,
      isDomestic: context.isDomestic,
      hasBreakfast,
    });
    injectedItems.push(...departureItems);
  }

  // 6. Fix 6: Pre-dawn safety cap — remove non-logistics items before 6 AM
  if (dayData.schedule) {
    dayData.schedule = filterPreDawnItems(dayData.schedule);
  }

  // 7. Hotel-return card for regular middle days
  if (!isFirstDay && !isLastDay && !context.isInterCityTravelDay && context.hotelName) {
    const allEndTimes = [...(dayData.schedule || []), ...(dayData.meals || [])]
      .map(it => (parseTimeToMinutes(it.time) || 0) + (parseDurationToMinutes(it.duration) || 60))
      .filter(t => t > 0);
    const lastEndMin = allEndTimes.length > 0 ? Math.max(...allEndTimes) : 20 * 60;
    const returnMin = Math.min(Math.max(lastEndMin + 30, 20 * 60), 22 * 60);
    injectedItems.push({
      type: 'hotel-return',
      time: minutesToTimeStr(returnMin),
      activity: `Return to ${context.hotelName}`,
      description: 'End of day',
      location: context.hotelName,
      duration: '—',
      sortTime: returnMin,
    });
  }

  // 8. Breakfast injection — if no breakfast exists in meals or schedule
  // Skip on arrival day (Day 1) — user arrives mid-day, breakfast doesn't make sense
  // Skip if hotel check-in hasn't happened yet
  {
    const hasBreakfast = [
      ...(dayData.schedule || []),
      ...(dayData.meals || []),
      ...injectedItems,
    ].some(it =>
      (it.meal || it.activity || it.mealType || '').toLowerCase().includes('breakfast')
    );
    const isOvernightArrival = context.isOvernightFlight && context.arrivalDayOffset > 0;
    const checkinItem = injectedItems.find(it => it.type === 'hotel-checkin');
    const checkinMin = checkinItem ? (checkinItem.sortTime ?? parseTimeToMinutes(checkinItem.time)) : null;

    // Don't inject breakfast on Day 1 (arrival), overnight arrival days, or if no hotel
    if (!hasBreakfast && !isOvernightArrival && !isFirstDay && context.hotelName) {
      let breakfastMin = 8 * 60 + 30; // default 08:30
      if (isLastDay && context.departureTime) {
        // Departure day: 90 min before checkout
        const depMin = parseTimeToMinutes(context.departureTime) || 18 * 60;
        const checkoutMin = depMin - 150; // 2.5hrs before flight = checkout
        breakfastMin = Math.max(checkoutMin - 90, 7 * 60);
      }
      // Ensure breakfast is after check-in (if check-in is on same day)
      if (checkinMin && breakfastMin < checkinMin + 30) {
        breakfastMin = checkinMin + 30;
      }
      // Collision avoidance: if first activity is before breakfast, shift earlier
      const firstActivityMin = [...(dayData.schedule || []), ...(dayData.meals || [])]
        .map(it => parseTimeToMinutes(it.time))
        .filter(t => t > 0)
        .sort((a, b) => a - b)[0];
      if (firstActivityMin && firstActivityMin <= breakfastMin + 45) {
        breakfastMin = Math.max(firstActivityMin - 60, 7 * 60);
      }
      // Final guard: don't inject if it would be after noon (too late for breakfast)
      if (breakfastMin < 12 * 60) {
        injectedItems.push({
          type: 'meal',
          time: minutesToTimeStr(breakfastMin),
          meal: 'Breakfast',
          mealType: 'breakfast',
          activity: 'Breakfast',
          restaurant: `Breakfast at ${context.hotelName}`,
          description: isLastDay ? 'Enjoy breakfast before checking out' : 'Start the day with breakfast',
          duration: isLastDay ? '45 minutes' : '1 hour',
          location: context.hotelName,
          sortTime: breakfastMin,
          _injectedBreakfast: true,
        });
      }
    }
  }

  // 9. Free-time gap detection
  {
    const LOGISTICS_TYPES = new Set([
      'arrival-customs', 'arrival-domestic', 'arrival-baggage', 'hotel-checkin',
      'checkout', 'airport-transit', 'airport-arrival', 'departure',
      'recovery-note', 'overnight-journey', 'overnight-arrival', 'hotel-return',
      'inter-city-travel', 'road-trip', 'transit', 'long-distance-transfer',
    ]);

    // Build full timeline for gap analysis
    const timeline = [
      ...(dayData.schedule || []).map(s => ({ ...s, _src: 'schedule' })),
      ...(dayData.meals || []).map(m => ({ ...m, _src: 'meals' })),
      ...injectedItems.map(i => ({ ...i, _src: 'injected' })),
    ]
      .filter(it => it.time)
      .sort((a, b) => (a.sortTime ?? parseTimeToMinutes(a.time)) - (b.sortTime ?? parseTimeToMinutes(b.time)));

    for (let i = 0; i < timeline.length - 1; i++) {
      const curr = timeline[i];
      const next = timeline[i + 1];

      // Skip gaps after/before logistics
      if (LOGISTICS_TYPES.has(curr.type) || LOGISTICS_TYPES.has(next.type)) continue;

      const currStart = curr.sortTime ?? parseTimeToMinutes(curr.time);
      const currDur = parseDurationToMinutes(curr.duration) || 60;
      const currEnd = currStart + currDur;
      const nextStart = next.sortTime ?? parseTimeToMinutes(next.time);
      const gap = nextStart - currEnd;

      if (gap >= 60 && gap <= 240) {
        const cappedGap = Math.min(gap, 120);
        const gapHrs = Math.floor(cappedGap / 60);
        const gapMins = cappedGap % 60;
        const gapStr = gapHrs > 0 ? `${gapHrs}hr${gapMins > 0 ? ` ${gapMins}min` : ''}` : `${gapMins}min`;

        // Use AI-generated suggestions if available (from backend), else time-appropriate defaults
        const hour = Math.floor(currEnd / 60);
        const period = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
        const aiSuggestions = dayData.freeTimeSuggestions?.[period];
        let suggestions;
        if (Array.isArray(aiSuggestions) && aiSuggestions.length > 0) {
          suggestions = aiSuggestions.slice(0, 3).map(s => ({
            emoji: s.emoji || '✨',
            label: s.label || s.name || 'Explore nearby',
            description: s.description || '',
          }));
        } else if (hour < 12) {
          suggestions = [
            { emoji: '☕', label: 'Find a local café' },
            { emoji: '📸', label: 'Photo walk nearby' },
            { emoji: '🛍️', label: 'Browse a local market' },
          ];
        } else if (hour < 17) {
          suggestions = [
            { emoji: '🏛️', label: 'Visit a nearby landmark' },
            { emoji: '☕', label: 'Afternoon coffee break' },
            { emoji: '🛍️', label: 'Shopping & souvenirs' },
          ];
        } else {
          suggestions = [
            { emoji: '🌅', label: 'Catch the sunset' },
            { emoji: '🍷', label: 'Aperitif at a bar' },
            { emoji: '🚶', label: 'Evening stroll' },
          ];
        }

        injectedItems.push({
          type: 'free-time',
          time: minutesToTimeStr(currEnd),
          activity: 'Free Time',
          duration: gapStr,
          durationMinutes: cappedGap,
          sortTime: currEnd,
          suggestions,
          baseCity: currentCity,
          fromLocation: curr.activity || curr.restaurant || '',
          toLocation: next.activity || next.restaurant || '',
        });
      }
    }
  }

  // 10. Activity time-window filtering
  {
    let cutoffStart = 0;
    let cutoffEnd = 24 * 60;

    // After hotel check-in: no activities until 30 min after
    const checkinItem = injectedItems.find(it => it.type === 'hotel-checkin');
    if (checkinItem) {
      const checkinEnd = (checkinItem.sortTime ?? parseTimeToMinutes(checkinItem.time)) + 30;
      cutoffStart = Math.max(cutoffStart, checkinEnd);
    }

    // Before checkout/departure: no activities after
    const checkoutItem = injectedItems.find(it => it.type === 'checkout');
    if (checkoutItem) {
      cutoffEnd = Math.min(cutoffEnd, checkoutItem.sortTime ?? parseTimeToMinutes(checkoutItem.time));
    }
    const departItem = injectedItems.find(it => it.type === 'airport-transit' || it.type === 'departure');
    if (departItem) {
      cutoffEnd = Math.min(cutoffEnd, departItem.sortTime ?? parseTimeToMinutes(departItem.time));
    }

    // Overnight arrival recovery
    if (context.isOvernightFlight && context.arrivalDayOffset > 0 && context.timezoneDiff) {
      const tzShift = Math.abs(context.timezoneDiff);
      const recoveryMin = tzShift >= 8 ? 180 : tzShift >= 4 ? 120 : 60;
      const recoveryItem = injectedItems.find(it => it.type === 'recovery-note');
      if (recoveryItem) {
        const recovEnd = (recoveryItem.sortTime ?? parseTimeToMinutes(recoveryItem.time)) + recoveryMin;
        cutoffStart = Math.max(cutoffStart, recovEnd);
      }
    }

    // Remove activities outside valid window on arrival/departure days
    // Day 1: remove pre-checkin items (can't eat at hotel before arriving)
    // Last day: remove post-checkout items (need to leave for airport)
    if (dayData.schedule && (cutoffStart > 0 || cutoffEnd < 24 * 60)) {
      dayData.schedule = dayData.schedule.filter(item => {
        const itemMin = parseTimeToMinutes(item.time);
        if (isFirstDay && itemMin < cutoffStart) return false; // Before arrival/checkin
        if (isLastDay && itemMin >= cutoffEnd) return false;   // After checkout/departure
        return true;
      });
    }
    // Also filter meals outside the window
    if (dayData.meals && (cutoffStart > 0 || cutoffEnd < 24 * 60)) {
      dayData.meals = (dayData.meals || []).filter(meal => {
        const mealMin = parseTimeToMinutes(meal.time);
        if (isFirstDay && mealMin < cutoffStart) return false;
        if (isLastDay && mealMin >= cutoffEnd) return false;
        return true;
      });
    }
  }

  return {
    ...dayData,
    injectedItems,
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * Convert minutes since midnight to "HH:MM" string.
 * @param {number} minutes
 * @returns {string}
 */
function minutesToTimeStr(minutes) {
  const clamped = Math.max(0, Math.min(1439, Math.round(minutes)));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}
